import type { JobCategoryRef } from '@seek/job-category';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  SECTION_PAY_DETAILS,
  SECTION_PRIVATE_AD,
  SECTION_CLASSIFY,
} from 'src/app/shared/constants/pageSectionName';
import { PAGE_ID_CLASSIFY } from 'src/app/shared/constants/pageUid';
import handleValidation from 'src/app/utils/handleValidation';
import { Form } from 'src/app/v1/views/components/Form';
import { JobCategory } from 'src/app/v1/views/components/JobCategory';
import { JobLocation } from 'src/app/v1/views/components/JobLocation';
import { JobTitle } from 'src/app/v1/views/components/JobTitle';
import { PageButtons } from 'src/app/v1/views/components/PageButtons';
import { Pay } from 'src/app/v1/views/components/Pay';
import { PhoneNumber } from 'src/app/v1/views/components/PhoneNumber';
import { PricePanel } from 'src/app/v1/views/components/PricePanel';
import { PrivateAdToggle } from 'src/app/v1/views/components/PrivateAdToggle';
import { WorkType } from 'src/app/v1/views/components/WorkType';
import { Workplace } from 'src/app/v1/views/components/Workplace';
import { withDesktopEnhancers } from 'src/app/v1/views/enhancers/withDesktopEnhancers';
import { useHandleEmailVerification } from 'src/app/v1/views/hooks/useHandleEmailVerification';
import { usePrivateAdFeature } from 'src/app/v1/views/hooks/usePrivateAdFeature';
import PageLayout from 'src/app/v1/views/layouts/PageLayout';
import { PageSection } from 'src/app/v1/views/sections/PageSection';
import type { AppState } from 'src/types/appState';
import type {
  JobLocationRef,
  JobTitleRef,
  PhoneNumberRef,
  WorkplaceRef,
} from 'src/types/formFields';
import type {
  CreateFlowPageContainer,
  CreateFlowPageProps,
} from 'src/types/pages';

import translations from './.vocab';

import * as styles from './index.css';

const ClassifyPage = ({
  handleDispatchDraft,
  handleNextPageRoute,
  handleProgrammaticRouteChange,
  handleSaveDraftFailed,
  pageErrorMessage,
}: CreateFlowPageProps) => {
  const { t } = useTranslations(translations);
  const { categoryId, draftId, isConfidential, jobLocationCountry, hidePrice } =
    useSelector(
      (state: AppState) => ({
        categoryId: state.formData.categoryId,
        draftId: state.session.draftId,
        isConfidential: state.formData.isConfidential,
        jobLocationCountry: state.session.jobLocationCountry,
        hidePrice: state.session.hirerAccount?.hidePrice,
      }),
      shallowEqual,
    );

  const { showPrivateAdToggle } = usePrivateAdFeature();
  const jobTitleRef = useRef<JobTitleRef>(null);
  const jobCategoryRef = useRef<JobCategoryRef>(null);
  const jobLocationRef = useRef<JobLocationRef>(null);
  const workplaceRef = useRef<WorkplaceRef>(null);
  const payRef = useRef(null);
  const phoneNumberRef = useRef<PhoneNumberRef>(null);

  const roleInfoComponents = [
    jobTitleRef,
    jobLocationRef,
    jobCategoryRef,
    workplaceRef,
  ];
  const containerComponent: CreateFlowPageContainer[] = [
    {
      containerId: SECTION_CLASSIFY,
      components: roleInfoComponents,
    },
    {
      containerId: SECTION_PAY_DETAILS,
      components: [payRef],
    },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [showJobCategory, setShowJobCategory] = useState(false);
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);

  const containerComponentMapping: CreateFlowPageContainer[] = showPhoneNumber
    ? [
        ...containerComponent,
        {
          containerId: SECTION_PRIVATE_AD,
          components: [phoneNumberRef],
        },
      ]
    : containerComponent;

  const setShowJobCategoryState = (
    draftIdentifier?: string,
    categoryIdentifier?: string,
  ) => {
    if (draftIdentifier || categoryIdentifier) {
      setShowJobCategory(true);
    }
  };

  const setShowPhoneNumberState = (
    isConfidentialState?: boolean,
    jobLocationCountryState?: string,
  ) => {
    if (isConfidentialState && jobLocationCountryState === 'hk') {
      setShowPhoneNumber(true);
    } else {
      setShowPhoneNumber(false);
    }
  };
  useEffect(() => {
    setShowJobCategoryState(draftId, categoryId);
  }, [categoryId, draftId]);

  useEffect(() => {
    setShowPhoneNumberState(isConfidential, jobLocationCountry);
  }, [isConfidential, jobLocationCountry]);

  const { handleEmailVerification } = useHandleEmailVerification();

  const handleComponentValidation = async (skipIfNull = false) => {
    setIsLoading(true);
    await handleEmailVerification();

    const validationResult = handleValidation(
      containerComponentMapping,
      skipIfNull,
    );
    setIsLoading(false);

    return validationResult;
  };

  return (
    <PageLayout
      hasAside
      hasProgressBar
      handleProgrammaticRouteChange={handleProgrammaticRouteChange}
      handleValidation={handleComponentValidation}
      pageErrorMessage={pageErrorMessage}
      handleDispatchDraft={handleDispatchDraft}
    >
      <Form
        handleNextPageRoute={handleNextPageRoute}
        handleValidation={handleComponentValidation}
      >
        <Stack space="xlarge">
          <Stack space="large">
            <Heading level="3" component="h2">
              {t('classify-page-heading')}
            </Heading>
            <Columns space="large" collapseBelow="tablet">
              <Column width="2/3">
                <Stack space="large">
                  <PageSection sectionName={SECTION_CLASSIFY}>
                    <Stack space="small">
                      <JobTitle ref={jobTitleRef} />
                      <JobLocation ref={jobLocationRef} />
                      {<Workplace ref={workplaceRef} />}
                      {showJobCategory && <JobCategory ref={jobCategoryRef} />}
                      <WorkType />
                      <Pay ref={payRef} />
                    </Stack>
                  </PageSection>
                  {(showPrivateAdToggle || showPhoneNumber) && (
                    <PageSection sectionName={SECTION_PRIVATE_AD}>
                      <Stack space="large">
                        <Stack space="small">
                          <Text weight="strong">
                            {t('classify-page-private-ad-section-title')}
                          </Text>
                          {showPrivateAdToggle && <PrivateAdToggle />}
                        </Stack>
                        {showPhoneNumber && (
                          <PhoneNumber ref={phoneNumberRef} />
                        )}
                      </Stack>
                    </PageSection>
                  )}
                </Stack>
              </Column>
              <Column width="1/3">
                {!hidePrice && (
                  <Box className={styles.pricePanel}>
                    <PricePanel />
                  </Box>
                )}
              </Column>
            </Columns>
          </Stack>
          <PageButtons
            isLoading={isLoading}
            handleSaveDraftFailed={handleSaveDraftFailed}
            handleValidation={handleComponentValidation}
          />
        </Stack>
      </Form>
    </PageLayout>
  );
};

export default withDesktopEnhancers(PAGE_ID_CLASSIFY)(ClassifyPage);
